import React, { memo, useMemo } from 'react';
import { Box, Button, FooterActionBar } from '@saviynt/design-system';
import cx from 'classnames';

import { useInvertedLocalize } from '../../../../../shared/hooks';

import {
  type ActiveScreenType,
  REMEDIATE_MODE,
  type RemediateMode,
} from './ViolationsRemediateAction';

interface RemediationFooterProps {
  activeScreen: ActiveScreenType;
  isError: boolean;
  remediateMode: RemediateMode;
  isDisabled?: boolean;
  sectionRefIsScrolled: boolean;
  isButtonActive: boolean;
  isRequest: boolean;
  isAllowedSaveSimulation: boolean;
  goNextScreen: () => void;
  goBackScreen: () => void;
  handleSaveSimulation: () => void;
}

/**
 * RemediationFooter
 */
function RemediationFooter({
  activeScreen,
  isError,
  remediateMode,
  isDisabled,
  sectionRefIsScrolled,
  isButtonActive,
  isRequest,
  isAllowedSaveSimulation,
  goNextScreen,
  goBackScreen,
  handleSaveSimulation,
}: RemediationFooterProps) {
  const l = useInvertedLocalize();

  const isActionButtonEnabled = useMemo(() => {
    if (activeScreen === 2 || isButtonActive) {
      return true;
    }

    return false;
  }, [activeScreen, isButtonActive]);
  const actionButtonText = useMemo(() => {
    // REMEDIATE OPTIONS: "=No Action,1=Create Request,2=Create Task"
    if (activeScreen === 2) {
      return l('Next');
    }

    if (isRequest) {
      return l('Remediate');
    }

    if (remediateMode === REMEDIATE_MODE.MUL_FUN_ENTS) {
      return l('Submit Request');
    }

    if (remediateMode === REMEDIATE_MODE.COM_ASG_ENTS) {
      return l('Request to Revoke');
    }

    return l('Proceed');
  }, [l, activeScreen, isRequest, remediateMode]);

  const footerClasses = cx(
    'Sod-RemediateActionModal-footer',
    `Sod-RemediateActionModal-footer--isScreen${activeScreen}`,
    isError && 'Sod-RemediateActionModal-footer--isError'
  );

  return (
    <FooterActionBar
      size='large'
      className={footerClasses}
      isScrolled={sectionRefIsScrolled}>
      <div />
      <div className='Sod-RemediateActionModal-footerActionButtonsBox'>
        <Button type='button' kind='subtle' onClick={goBackScreen}>
          {l('Back')}
        </Button>
        <Box tag='div' className='Sod-RemediateActionModal-footerActionButtons'>
          {activeScreen === 2 && isAllowedSaveSimulation && (
            <Button
              type='button'
              kind='outlined'
              size='medium'
              isDisabled={isDisabled}
              onClick={handleSaveSimulation}>
              {l('Save to Simulation')}
            </Button>
          )}
          {activeScreen >= 2 && (
            <Button
              type='button'
              kind={isActionButtonEnabled ? 'filled' : 'outlined'}
              size='medium'
              onClick={goNextScreen}>
              {actionButtonText}
            </Button>
          )}
        </Box>
      </div>
    </FooterActionBar>
  );
}

RemediationFooter.defaultProps = {
  isDisabled: false,
};

export default memo(RemediationFooter);
